import React, { useState } from 'react';
import { Stack, Typography, Button, List, ListItem, IconButton, Avatar, useTheme, ListItemButton, Menu, MenuItem, ListItemIcon, CircularProgress } from '@mui/material';
import { Add, MoreVert, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { UsedIn } from '@domain/product/models/ProductComponent';
import { useNavigate } from 'react-router-dom';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { useToast } from '@shared/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import EditProductUsedInBottomSheet from '../used-in/EditProductUsedInBottomSheet';
import { useProductUpdate } from '@features/products/hooks/useProductUpdate';

interface ProductUsedInListProps {
  productId: string;
  usedIn: UsedIn[] | undefined;
  onAddUsedIn: () => void;
}

const ProductUsedInList: React.FC<ProductUsedInListProps> = ({
  productId,
  usedIn,
  onAddUsedIn,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { removeProductPart, isRemovingProductPart } = useProductUpdate();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<UsedIn | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: UsedIn) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
  };

  const handleEdit = () => {
    if (!selectedItem?.usedIn.id) {
      handleMenuClose();
      return;
    }
    const item = selectedItem;
    const parentProductId = item.usedIn.id;
    handleMenuClose();
    openBottomSheet({
      title: 'Edit Quantity',
      component: (
        <EditProductUsedInBottomSheet
          item={item}
          onSave={async (quantity) => {
            await container.productRepository.removeProductPart(parentProductId, item.usedIn.id!);
            await container.productRepository.addProductParts(parentProductId, [
              { productId, quantity },
            ]);
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
            toast.success('Quantity updated');
          }}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.SM,
    });
  };

  const handleRemove = () => {
    if (!selectedItem?.usedIn.id) {
      handleMenuClose();
      return;
    }

    handleMenuClose();
    removeProductPart({ productId: selectedItem.usedIn.id!, partId: productId });
  };

  return (
    <BorderedBlock
      sx={{ p: 0 }}
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Used In</Typography>
          <Button endIcon={<Add />} variant="text" color="primary" onClick={onAddUsedIn}>
            Add to product
          </Button>
        </Stack>
      }
    >
      <List disablePadding>
        {(!usedIn || usedIn.length === 0) && (
          <ListItem sx={{ p: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Not used in any products. Add this product to other products.
            </Typography>
          </ListItem>
        )}
        {usedIn?.map((item: UsedIn) => (
          <ListItemButton
            key={item.usedIn.id}
            onClick={() => navigate(`/products/${item.usedIn.id}`)}
          >
            <Stack direction="row" alignItems="center" flexGrow={1} spacing={2} p={2}>
              <Avatar
                variant="rounded"
                src={item.usedIn.image}
                sx={{
                  width: 40,
                  height: 40,
                  border: 1,
                  borderColor: theme.palette.divider,
                }}
              />
              <Typography variant="body2">{item.usedIn.title || item.usedIn.ean}</Typography>
              <Typography variant="caption" color="text.secondary">
                {item.quantity}×
              </Typography>
            </Stack>
            {isRemovingProductPart ? (
              <CircularProgress size={20} />
            ) : (
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, item)}
                sx={{ mr: 1 }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            )}
          </ListItemButton>
        ))}
      </List>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleRemove}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography variant="body2" color="error">Remove</Typography>
        </MenuItem>
      </Menu>
    </BorderedBlock>
  );
};

export default ProductUsedInList;
